import React, { useEffect, useState } from "react";
import { CircularProgress, Box, Typography } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import confetti from "canvas-confetti";
import "./LoadingOverlay.scss";

interface LoadingOverlayProps {
    isLoading: boolean;
    message?: string;
    success?: boolean;
    successMessage?: string;
    onSuccessComplete?: () => void;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
    isLoading,
    message = "Loading...",
    success = false,
    successMessage = "Success!",
    onSuccessComplete,
}) => {
    const [showSuccess, setShowSuccess] = useState(false);
    const [hasShownConfetti, setHasShownConfetti] = useState(false);
    const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

    // Clean up timer on unmount
    useEffect(() => {
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [timer]);

    // Reset hasShownConfetti when success becomes false
    useEffect(() => {
        if (!success) {
            setHasShownConfetti(false);
        }
    }, [success]);

    // Handle success state
    useEffect(() => {
        // Clear existing timer if success state changes
        if (timer) {
            clearTimeout(timer);
            setTimer(null);
        }

        if (success && !showSuccess && !hasShownConfetti) {
            setShowSuccess(true);
            setHasShownConfetti(true);

            // Trigger confetti animation
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 },
            });

            // Hide success message after 3 seconds
            setTimeout(() => {
                setShowSuccess(false);
                if (onSuccessComplete) {
                    onSuccessComplete();
                }
            }, 3000);
        }
    }, [success, showSuccess, onSuccessComplete, hasShownConfetti]);

    if (!isLoading && !showSuccess) return null;

    return (
        <div className="loading-overlay">
            <Box
                className={`loading-content ${showSuccess ? "success" : ""}`}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
            >
                {showSuccess ? (
                    <>
                        <CheckCircle
                            sx={{
                                fontSize: 60,
                                color: "#9f8f5e",
                                marginBottom: 2,
                                animation: "scaleIn 0.5s ease-out",
                            }}
                        />
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{
                                color: "white",
                                textAlign: "center",
                                maxWidth: "80%",
                                animation: "fadeIn 0.5s ease-out",
                            }}
                        >
                            {successMessage}
                        </Typography>
                    </>
                ) : (
                    <>
                        <CircularProgress
                            size={60}
                            thickness={4}
                            sx={{
                                color: "#9f8f5e",
                                marginBottom: 2,
                            }}
                        />
                        {message && (
                            <Typography
                                variant="h6"
                                component="div"
                                sx={{
                                    color: "white",
                                    textAlign: "center",
                                    maxWidth: "80%",
                                }}
                            >
                                {message}
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </div>
    );
};

export default LoadingOverlay;
